import AiNotesIcon from "@shared/svg/ai-notetaker-active.svg?react";
import ChevronDownIcon from "@shared/svg/chevron-down.svg?react";
import CustomFieldsIcon from "@shared/svg/custom-fields.svg?react";
import DescriptionIcon from "@shared/svg/left-align.svg?react";
import PartIcon from "@shared/svg/part.svg?react";
import ProcedureIcon from "@shared/svg/procedures.svg?react";
import Label, { LABEL_SIZES } from "@shared/ui/Label";
import { Paperclip } from "phosphor-react";
import { useCallback, useEffect, useRef, useState } from "react";

/** Matches the `duration-200` on the open/close transition below. */
const TRANSITION_MS = 200;

export const ACCORDION_VARIANTS = {
  AI_NOTES: "aiNotes",
  PROCEDURES: "procedures",
  DESCRIPTION: "description",
  CUSTOM_FIELDS: "customFields",
  ATTACHMENTS: "attachments",
  PARTS: "parts",
  OTHER: "other",
} as const;

export type AccordionVariantType =
  (typeof ACCORDION_VARIANTS)[keyof typeof ACCORDION_VARIANTS];

const getIcon = (variant: AccordionVariantType) => {
  switch (variant) {
    // Ships its own circular background, so it fills the 24px icon slot.
    case ACCORDION_VARIANTS.AI_NOTES:
      return <AiNotesIcon width={16} height={16} />;

    case ACCORDION_VARIANTS.PROCEDURES:
      return <ProcedureIcon width={16} height={16} />;

    case ACCORDION_VARIANTS.DESCRIPTION:
      return <DescriptionIcon width={16} height={16} />;

    case ACCORDION_VARIANTS.CUSTOM_FIELDS:
      return <CustomFieldsIcon width={16} height={16} />;

    case ACCORDION_VARIANTS.PARTS:
      return <PartIcon width={16} height={16} />;

    case ACCORDION_VARIANTS.ATTACHMENTS:
      return <Paperclip size={16} />;

    case ACCORDION_VARIANTS.OTHER:
      return null;
  }
};

type AccordionPropsType = {
  variant?: AccordionVariantType;
  title: string;
  icon?: React.ReactNode;
  /** Optional node rendered immediately after the title label (e.g. an info icon). */
  titleSuffix?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  /**
   * Marks the section as having no content yet. An empty accordion starts
   * collapsed regardless of `defaultExpanded`, and opens on its own once content
   * arrives (e.g. the first item gets assigned) — unless the user already
   * toggled it.
   */
  isEmpty?: boolean;
  showBorder?: boolean;
  className?: string;
  contentClassName?: string;
  showIcon?: boolean;
  /** Use `trailingComponent` for new implementations. Kept for backward compatibility. */
  rightIcon?: React.ReactNode;
  /** Slot for action buttons (e.g. "+ Assign") rendered between the title and chevron. Clicks here do not toggle the accordion. */
  trailingComponent?: React.ReactNode;
};

const Accordion = ({
  variant = ACCORDION_VARIANTS.OTHER,
  title,
  icon = null,
  titleSuffix = null,
  children,
  defaultExpanded = false,
  isEmpty = false,
  showBorder = true,
  className = "",
  contentClassName = "",
  showIcon = true,
  rightIcon = null,
  trailingComponent = null,
}: AccordionPropsType) => {
  const initiallyExpanded = defaultExpanded && !isEmpty;
  const [expanded, setExpanded] = useState<boolean>(initiallyExpanded);
  // The grid-rows animation only works if the content is clipped while it
  // plays — but that same clip cuts off anything a child renders outside the
  // panel, e.g. an open dropdown. Release it once the panel has finished
  // opening, and re-apply it the moment a collapse starts.
  const [clipContent, setClipContent] = useState<boolean>(!initiallyExpanded);
  const userToggled = useRef(false);
  const wasEmpty = useRef(isEmpty);

  const toggleAccordion = useCallback(() => {
    userToggled.current = true;
    setExpanded((prev) => !prev);
  }, []);

  // Content just arrived in a section that was collapsed for being empty —
  // reveal it, as long as the user hasn't chosen a state themselves.
  useEffect(() => {
    if (
      wasEmpty.current &&
      !isEmpty &&
      defaultExpanded &&
      !userToggled.current
    ) {
      setExpanded(true);
    }
    wasEmpty.current = isEmpty;
  }, [isEmpty, defaultExpanded]);

  useEffect(() => {
    if (!expanded) {
      setClipContent(true);
      return undefined;
    }

    const timer = setTimeout(() => setClipContent(false), TRANSITION_MS);
    return () => clearTimeout(timer);
  }, [expanded]);

  const actionSlot = trailingComponent ?? rightIcon;

  return (
    <div
      className={`flex flex-col ${
        showBorder
          ? "border border-solid border-primary bg-primary rounded-lg"
          : ""
      } ${className}`}
    >
      <div
        className={`flex w-full items-center gap-md ${
          showBorder ? "p-lg" : "px-lg py-md"
        }`}
        aria-expanded={expanded}
      >
        <div
          role="button"
          tabIndex={0}
          className="flex flex-1 items-center gap-md min-w-0 cursor-pointer"
          onClick={toggleAccordion}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleAccordion();
          }}
        >
          {showIcon && (
            <div className="shrink-0 w-2xl h-2xl bg-brand-lightest text-brand flex items-center justify-center rounded-[12px]">
              {icon || getIcon(variant)}
            </div>
          )}
          <Label size={LABEL_SIZES.MEDIUM}>{title}</Label>
          {titleSuffix}
        </div>

        {actionSlot && <div className="shrink-0">{actionSlot}</div>}

        <div
          role="button"
          tabIndex={0}
          className="shrink-0 cursor-pointer"
          onClick={toggleAccordion}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") toggleAccordion();
          }}
        >
          <ChevronDownIcon
            width={20}
            height={20}
            className={`text-secondary transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </div>
      </div>

      {/*
        `grid-cols-[minmax(0,1fr)]` caps the content column at the accordion's
        own width. An `auto` column would instead size to the content's
        max-content — a `truncate`d title is `white-space: nowrap`, so that
        measures the untruncated string and the row spills out of the card. The
        clip used to hide the spill; now that it is released once expanded, the
        column has to be bounded properly.
      */}
      <div
        className={`grid grid-cols-[minmax(0,1fr)] transition-grid duration-200 ease-out ${
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
        }`}
      >
        <div
          className={`min-w-0 ${
            clipContent ? "overflow-hidden" : "overflow-visible"
          }`}
        >
          <div className={`px-lg pb-lg pt-0 ${contentClassName}`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Accordion;
